import { useCallback, useEffect, useState } from 'react'

import * as doctorApi from 'features/doctors/api.client'
import { NewOrUpdatedDoctor } from 'features/doctors/types'
import { formatCpf, validateCpf } from 'utilities/cpf.util'
import {
  formatPhoneNumber,
  validatePhoneNumber,
} from 'utilities/phoneNumber.util'
import { removeAlphabeticCharacteres } from 'utilities/string.util'

/**
 * Doctor data (CPF and phone) of the logged user. Saved through the
 * /doctors endpoints, separately from the user account.
 */
export const useDoctorData = (enabled: boolean) => {
  const [doctor, setDoctor] = useState<NewOrUpdatedDoctor>({
    cpf: '',
    phoneNumber: '',
  })
  const [created, setCreated] = useState(false)
  const [loadError, setLoadError] = useState(false)

  useEffect(() => {
    if (!enabled) {
      return
    }

    doctorApi
      .getDoctorFromLoggedUser()
      .then(({ doctorCreated, doctor }) => {
        setCreated(doctorCreated)

        if (doctorCreated) {
          setDoctor({ cpf: doctor.cpf, phoneNumber: doctor.phoneNumber ?? '' })
        }
      })
      .catch(() => setLoadError(true))
  }, [enabled])

  const isValid =
    validateCpf(formatCpf(doctor.cpf)) &&
    validatePhoneNumber(doctor.phoneNumber)

  const save = useCallback(async () => {
    const payload: NewOrUpdatedDoctor = {
      cpf: removeAlphabeticCharacteres(formatCpf(doctor.cpf)),
      phoneNumber: removeAlphabeticCharacteres(
        formatPhoneNumber(doctor.phoneNumber)
      ),
    }

    const saved = created
      ? await doctorApi.update(payload)
      : await doctorApi.create(payload)

    setDoctor({
      cpf: saved.cpf,
      phoneNumber: saved.phoneNumber ?? payload.phoneNumber,
    })
    setCreated(true)
  }, [created, doctor])

  return { doctor, setDoctor, created, isValid, loadError, save }
}
